import {actionTool, noInput, objectInput, readyForPerson, requireFilled, stringInput, waitForSecrets} from "@/functions/webMcpTool";
import {chooseOption, cleanText, fillInput, findButtonByText, findElement, formatPhoneForInput, isVisible, press, pressButtonWithText, setChecked} from "@/functions/webMcpDom";
import {TERMS_REASON} from "@/functions/webMcpToolsSignIn";
import {describeCurrentStep} from "@/functions/webMcpStep";
import getBrandName from "@/functions/getBrandName";
import {ToolError} from "@/functions/webMcp";

const DUPLICATE_EMAIL_CHOICES = {
	sign_in: 'sign in.',
	recover_account: 'Recover my old account',
	create_new_account_anyway: 'Create new account with this email',
}

const buildCreateAccount = () => actionTool({
	name: 'create_account',
	description: `Fill the ${getBrandName()} new-account form with the details the person gives you. The password goes into both password fields from the person or their password manager, never through this tool. Pressing Create new account agrees to the Terms of Use, the Privacy Policy and informational emails, so the tool stops there and returns ready_for_person; the person presses it, and the site runs its captcha.`,
	inputSchema: objectInput({
		email: stringInput('The person\'s email address.', {maxLength: 75}),
		username: stringInput('The username the person wants.', {maxLength: 128}),
		phone: stringInput('Optional 10-digit US mobile number, used for two-step verification.'),
		verifyPhoneBy: stringInput('How to verify the phone number, when one is given. Defaults to text.', {enum: ['text', 'call']}),
		rememberDevice: {type: 'boolean', description: 'Whether to remember this device so it skips two-step verification next time.'},
	}, ['email', 'username']),
	run: ({email, username, phone, verifyPhoneBy, rememberDevice}) => {
		const missing = requireFilled({email, username}, {email: 'Enter the person\'s email address.', username: 'Enter the username the person wants.'})
		if (missing) return missing
		fillInput('#email', email, 'email field')
		fillInput('#confirmEmail', email, 'confirm email field')
		fillInput('#username', username, 'username field')
		if (phone) {
			fillInput('#phone', formatPhoneForInput(phone), 'phone field')
			chooseOption(verifyPhoneBy === 'call' ? '#voiceOption' : '#smsOption', 'phone verification option')
		}
		if (typeof rememberDevice === 'boolean') {
			setChecked('#rememberDevice', rememberDevice, 'Remember this device checkbox')
		}
		return readyForPerson('#submitCreateUser', {secrets: ['#password', '#confirmPassword'], reason: TERMS_REASON})
	},
})

const duplicateEmailDialog = () => {
	const modal = document.querySelector('#duplicateEmailAccountModal:not(.modal-backdrop-hidden)')
	return modal && isVisible(modal) ? modal : null
}

export const resolveDuplicateEmail = (choices, createChoice) => ({choice}) => {
	const modal = duplicateEmailDialog()
	if (!modal) throw new ToolError('The "You already have an account with this email." message is not showing any more. Call get_auth_state.', 'PAGE_CHANGED', true)
	const text = choices[choice]
	if (!text) throw new ToolError(`Unknown choice "${choice}".`, 'INVALID_INPUT')
	const button = findButtonByText(text, modal)
	if (!button) throw new ToolError(`The "${text}" choice is not showing. Call get_auth_state.`, 'PAGE_CHANGED', true)
	if (choice === createChoice) {
		button.focus()
		return {result: {outcome: 'ready_for_person', button: cleanText(button), reason: `The person presses "${cleanText(button)}" themselves: ${TERMS_REASON} After that, call get_auth_state.`, step: describeCurrentStep()}}
	}
	button.click()
}

export const buildWhileDuplicateEmailShows = (build) => () => (duplicateEmailDialog() ? build() : [])

const buildResolveDuplicateEmail = buildWhileDuplicateEmailShows(() => actionTool({
	name: 'resolve_duplicate_email',
	description: `The page says this email already has a ${getBrandName()} account. Choose what to do: go to sign in, recover the old account, or create a new account with the same email anyway. Signing in and recovering are pressed for you; creating a new account agrees to the Terms of Use, so that one returns ready_for_person for the person to press.`,
	inputSchema: objectInput({
		choice: stringInput('What the person wants to do about the existing account.', {enum: Object.keys(DUPLICATE_EMAIL_CHOICES)}),
	}, ['choice']),
	run: resolveDuplicateEmail(DUPLICATE_EMAIL_CHOICES, 'create_new_account_anyway'),
}))

const buildChooseUsername = () => actionTool({
	name: 'choose_username',
	description: `Set the username the person chose for their ${getBrandName()} account. The site asks for this after some sign-ins, such as a first partner sign-in, before the person can continue to their tax return. Returns the next step, or the page's message if the username is taken or not allowed.`,
	inputSchema: objectInput({username: stringInput('The username the person wants.', {maxLength: 128})}, ['username']),
	run: ({username}) => {
		fillInput('#username', username, 'username field')
		return press('form button[type="submit"]', 'Continue button')
	},
})

const buildSetNewPassword = () => actionTool({
	name: 'set_new_password',
	description: `Save the new password when ${getBrandName()} requires a password change after sign-in. The new password goes into both fields from the person or their password manager, never through this tool, so it waits until both are filled, then saves. Returns the next step, or the page's message if the password doesn't meet the requirements shown on the page.`,
	inputSchema: noInput,
	run: () => waitForSecrets('set_new_password', ['#password', '#confirmPassword']) ?? press('button[type="submit"][form="updatePasswordForm"]', 'Save and continue button'),
})

const primaryPasskeyButton = () => findElement('.order-sm-2 button', 'passkey button')

const buildAddPasskey = () => actionTool({
	name: 'add_passkey',
	description: `Start creating a passkey for the person's ${getBrandName()} account on this device. The browser or password manager shows its own prompt, and only the person can approve it with their fingerprint, face, or device PIN. Tell them to approve it, then call get_auth_state.`,
	startsPasskeyPrompt: true,
	run: () => {
		const button = primaryPasskeyButton()
		if (cleanText(button) === 'Continue') {
			return {result: {error: 'A passkey was already created. Call continue_after_passkey.'}}
		}
		button.click()
	},
})

const buildSkipPasskey = () => actionTool({
	name: 'skip_passkey',
	description: `Decline the passkey offer for now and continue signing in to ${getBrandName()}, exactly as the Not Now button does. Nothing on the account changes, and the person can still add a passkey later from their account settings. The browser moves on to the tax return or the next required step.`,
	run: () => pressButtonWithText('Not Now'),
})

const buildContinueAfterPasskey = () => actionTool({
	name: 'continue_after_passkey',
	description: `Continue signing in to ${getBrandName()} after the person has created a passkey on this page. Only works once the page says the passkey was made; until then, use add_passkey or skip_passkey instead. The browser moves on to the tax return or the next required step.`,
	run: () => pressButtonWithText('Continue'),
})

export const createAccountTools = {
	NewUser: [buildCreateAccount, buildResolveDuplicateEmail],
	ChooseUsername: [buildChooseUsername],
	UpdatePassword: [buildSetNewPassword],
	PasskeyUpsell: [buildAddPasskey, buildSkipPasskey, buildContinueAfterPasskey],
}